<template>
  <section class="avatar-crop-page" role="dialog" aria-modal="true" :aria-label="$t('avatarCrop.title')">
    <header>
      <button type="button" :disabled="processing" @click="$emit('cancel')">{{ $t("avatarCrop.cancel") }}</button>
      <strong>{{ $t("avatarCrop.title") }}</strong>
      <button type="button" :disabled="processing || !ready" @click="confirmCrop">
        <i v-if="processing" class="el-icon-loading" />{{ $t("avatarCrop.confirm") }}
      </button>
    </header>

    <main>
      <p>{{ $t("avatarCrop.hint") }}</p>
      <div
        ref="viewport"
        class="avatar-crop-viewport"
        :class="{ ready }"
        @pointerdown="startDrag"
        @pointermove="moveDrag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
      >
        <img
          ref="image"
          :src="source"
          :alt="$t('avatarCrop.previewAlt')"
          :style="imageStyle"
          draggable="false"
          @load="handleImageLoad"
          @error="handleImageError"
        >
        <div class="avatar-crop-shade" />
        <div class="avatar-crop-ring"><i /><i /><i /><i /></div>
        <span v-if="!ready"><i class="el-icon-loading" /></span>
      </div>

      <div class="avatar-crop-zoom">
        <i class="el-icon-picture-outline" />
        <label>
          <span>{{ $t("avatarCrop.zoom") }}</span>
          <input v-model.number="zoom" type="range" min="1" max="3" step="0.01" :aria-label="$t('avatarCrop.zoom')" @input="handleZoom">
        </label>
        <i class="el-icon-zoom-in" />
      </div>
      <small>{{ $t("avatarCrop.dragHint") }}</small>
    </main>
  </section>
</template>

<script>
export default {
  name: "AvatarCropper",
  props: {
    source: { type: String, required: true }
  },
  data() {
    return {
      naturalWidth: 0,
      naturalHeight: 0,
      viewportSize: 0,
      zoom: 1,
      panX: 0,
      panY: 0,
      pointerId: null,
      dragX: 0,
      dragY: 0,
      processing: false
    }
  },
  computed: {
    ready() {
      return this.naturalWidth > 0 && this.naturalHeight > 0 && this.viewportSize > 0
    },
    baseScale() {
      if (!this.ready) return 1
      return Math.max(this.viewportSize / this.naturalWidth, this.viewportSize / this.naturalHeight)
    },
    renderedWidth() {
      return this.naturalWidth * this.baseScale * this.zoom
    },
    renderedHeight() {
      return this.naturalHeight * this.baseScale * this.zoom
    },
    imageStyle() {
      if (!this.ready) return {}
      return {
        width: `${this.renderedWidth}px`,
        height: `${this.renderedHeight}px`,
        transform: `translate(-50%, -50%) translate(${this.panX}px, ${this.panY}px)`
      }
    }
  },
  mounted() {
    window.addEventListener("resize", this.measureViewport)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measureViewport)
  },
  methods: {
    handleImageLoad(event) {
      const width = Number(event.target.naturalWidth) || 0
      const height = Number(event.target.naturalHeight) || 0
      if (!width || !height || width * height > 60000000) {
        this.$notice.warning(this.$t("avatarCrop.sourceTooLarge"))
        this.$emit("cancel")
        return
      }
      this.naturalWidth = width
      this.naturalHeight = height
      this.zoom = 1
      this.panX = 0
      this.panY = 0
      this.$nextTick(this.measureViewport)
    },
    handleImageError() {
      this.$notice.error(this.$t("avatarCrop.invalidImage"))
      this.$emit("cancel")
    },
    measureViewport() {
      if (!this.$refs.viewport) return
      this.viewportSize = this.$refs.viewport.clientWidth
      this.clampPan()
    },
    handleZoom() {
      this.clampPan()
    },
    clampPan() {
      if (!this.ready) return
      const limitX = Math.max(0, (this.renderedWidth - this.viewportSize) / 2)
      const limitY = Math.max(0, (this.renderedHeight - this.viewportSize) / 2)
      this.panX = Math.max(-limitX, Math.min(limitX, this.panX))
      this.panY = Math.max(-limitY, Math.min(limitY, this.panY))
    },
    startDrag(event) {
      if (!this.ready || this.processing || event.button > 0) return
      this.pointerId = event.pointerId
      this.dragX = event.clientX
      this.dragY = event.clientY
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    moveDrag(event) {
      if (this.pointerId !== event.pointerId) return
      this.panX += event.clientX - this.dragX
      this.panY += event.clientY - this.dragY
      this.dragX = event.clientX
      this.dragY = event.clientY
      this.clampPan()
    },
    endDrag(event) {
      if (this.pointerId !== event.pointerId) return
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      this.pointerId = null
    },
    async confirmCrop() {
      if (!this.ready || this.processing) return
      this.processing = true
      try {
        const outputSize = 800
        const ratio = outputSize / this.viewportSize
        const canvas = document.createElement("canvas")
        canvas.width = outputSize
        canvas.height = outputSize
        const context = canvas.getContext("2d", { alpha: false })
        if (!context) throw new Error("CANVAS_UNAVAILABLE")

        const left = (this.viewportSize - this.renderedWidth) / 2 + this.panX
        const top = (this.viewportSize - this.renderedHeight) / 2 + this.panY
        context.fillStyle = "#ffffff"
        context.fillRect(0, 0, outputSize, outputSize)
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = "high"
        context.drawImage(
          this.$refs.image,
          left * ratio,
          top * ratio,
          this.renderedWidth * ratio,
          this.renderedHeight * ratio
        )

        const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.9))
        if (!blob) throw new Error("CROP_FAILED")
        this.processing = false
        this.$emit("confirm", new File([blob], `avatar-${Date.now()}.jpg`, { type: "image/jpeg" }))
      } catch (error) {
        this.processing = false
        this.$notice.error(this.$t("avatarCrop.cropFailed"))
      }
    }
  }
}
</script>

<style scoped>
.avatar-crop-page{position:fixed;z-index:5200;inset:0;display:flex;flex-direction:column;color:#f8f4ed;background:radial-gradient(circle at 88% 0,rgba(255,64,88,.2),transparent 29%),#0b0c0f}
.avatar-crop-page>header{height:calc(60px + env(safe-area-inset-top));display:grid;grid-template-columns:minmax(72px,auto) 1fr minmax(72px,auto);align-items:end;padding:env(safe-area-inset-top) 15px 0;border-bottom:1px solid rgba(255,255,255,.07);background:rgba(11,12,15,.9);backdrop-filter:blur(18px)}
.avatar-crop-page>header button{height:60px;padding:0 4px;border:0;color:#a7a9b0;background:transparent;font-size:12px;font-weight:700}.avatar-crop-page>header button:last-child{color:#ff7183;text-align:end}.avatar-crop-page>header button:first-child{text-align:start}.avatar-crop-page>header button:disabled{opacity:.48}.avatar-crop-page>header strong{align-self:center;text-align:center;font-size:15px}
.avatar-crop-page main{flex:1;display:flex;flex-direction:column;align-items:center;overflow-y:auto;padding:28px 20px calc(32px + env(safe-area-inset-bottom))}
.avatar-crop-page main>p{max-width:310px;margin:0 0 22px;color:#898b92;font-size:11px;line-height:1.6;text-align:center}
.avatar-crop-viewport{position:relative;width:min(78vw,340px);aspect-ratio:1;flex:none;overflow:hidden;border-radius:22px;background:#17181d;box-shadow:0 24px 60px rgba(0,0,0,.38);touch-action:none;cursor:grab;user-select:none}.avatar-crop-viewport:active{cursor:grabbing}
.avatar-crop-viewport>img{position:absolute;z-index:1;top:50%;left:50%;max-width:none;max-height:none;object-fit:fill;pointer-events:none;will-change:width,height,transform}
.avatar-crop-shade{position:absolute;z-index:2;inset:0;border-radius:50%;box-shadow:0 0 0 100vmax rgba(6,7,9,.7);pointer-events:none}
.avatar-crop-ring{position:absolute;z-index:3;inset:8px;border:1px solid rgba(255,255,255,.78);border-radius:50%;pointer-events:none;box-shadow:0 0 0 1px rgba(0,0,0,.22),inset 0 0 36px rgba(0,0,0,.12)}
.avatar-crop-ring i{position:absolute;width:18px;height:18px;border-color:#ff6578}.avatar-crop-ring i:nth-child(1){top:15%;left:15%;border-top:2px solid;border-left:2px solid}.avatar-crop-ring i:nth-child(2){top:15%;right:15%;border-top:2px solid;border-right:2px solid}.avatar-crop-ring i:nth-child(3){right:15%;bottom:15%;border-right:2px solid;border-bottom:2px solid}.avatar-crop-ring i:nth-child(4){bottom:15%;left:15%;border-bottom:2px solid;border-left:2px solid}
.avatar-crop-viewport>span{position:absolute;z-index:4;inset:0;display:grid;place-items:center;color:#ff6478;font-size:24px}.avatar-crop-viewport.ready>span{display:none}
.avatar-crop-zoom{width:min(78vw,340px);display:grid;grid-template-columns:24px minmax(0,1fr) 24px;align-items:end;gap:10px;margin-top:26px;color:#777981}.avatar-crop-zoom>i:last-child{font-size:18px}.avatar-crop-zoom label{min-width:0}.avatar-crop-zoom label span{display:block;margin-bottom:8px;color:#a8aab0;font-size:10px;text-align:center}.avatar-crop-zoom input{width:100%;height:4px;margin:0;accent-color:#ff5368}
.avatar-crop-page main>small{max-width:300px;margin-top:13px;color:#676970;font-size:9px;line-height:1.5;text-align:center}
[dir=rtl] .avatar-crop-page>header button:first-child{text-align:right}[dir=rtl] .avatar-crop-page>header button:last-child{text-align:left}
@media(min-width:720px){.avatar-crop-page{left:50%;width:430px;transform:translateX(-50%);box-shadow:0 0 70px rgba(0,0,0,.5)}}
</style>
